import { Button, Group, Loader, SimpleGrid, Stack, Text } from '@mantine/core';
import { DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';

dayjs.extend(utc);
import { useTranslation } from 'react-i18next';
import {
  calculatePercentage,
  ChartContext,
  generateCSV,
  generateDateRange,
  getMetrics,
  MetricsItem,
  useBot,
} from '@mygpt/app';
import AllUsers from '@/components/Dashboard/Chart/AllUsers';
import ChatSatisfaction from '@/components/Dashboard/Chart/ChatSatisfaction';
import PopularityInteractions from '@/components/Dashboard/Chart/PopularityInteractions';
import TotalConversations from '@/components/Dashboard/Chart/TotalConversations';
import TotalConversationsHeatmap from '@/components/Dashboard/Chart/TotalConversationsHeatmap';
import TotalMessages from '@/components/Dashboard/Chart/TotalMessages';
import {
  SvgAllUsers,
  SvgChat,
  SvgChatMessages,
  SvgDownload,
  SvgNegativeFeedback,
  SvgProvideFeedback,
  SvgSatisfactionRate,
} from '@/components/Dashboard/icon';
import OverviewCard, { OverviewCardProps } from '@/components/Dashboard/OverviewCard';
import WidgetInstallTip from '@/components/Dashboard/WidgetInstallTip';
import DateSelector from '@/components/DateSelector';
import ContainerHeader from '@/components/Headers/ContainerHeader';

type TotalData = {
  totalConversations: number;
  totalMessages: number;
  allUsers: number;
  provideFeedback: number;
  negativeFeedback: number;
  userSatisfactionRate: number;
};
type TotalDataKeys = keyof TotalData;

const Dashboard = () => {
  const { t } = useTranslation();
  const { botId } = useBot();

  const [loading, setLoading] = useState(true);
  const [rangeDate, setRangeDate] = useState<string[]>([]);
  const [rangeDateTimeText, setRangeDateTimeText] = useState('');

  const [metricsData, setMetricsData] = useState<MetricsItem[]>([]);
  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  const CARD_LIST: OverviewCardProps[] = [
    {
      id: 'totalConversations',
      icon: SvgChat,
      value: 0,
      time: '',
      title: t('dashboard.card.total_conversations'),
      tip: t('dashboard.card.total_conversations_tip'),
    },
    {
      id: 'totalMessages',
      icon: SvgChatMessages,
      value: 0,
      time: '',
      title: t('dashboard.card.total_messages'),
      tip: t('dashboard.card.total_messages_tip'),
    },
    {
      id: 'allUsers',
      icon: SvgAllUsers,
      value: 0,
      time: '',
      title: t('dashboard.card.all_users'),
      tip: t('dashboard.card.all_users_tip'),
    },
    {
      id: 'provideFeedback',
      icon: SvgProvideFeedback,
      value: 0,
      valueUnit: '%',
      time: '',
      title: t('dashboard.card.provide_feedback'),
    },
    {
      id: 'negativeFeedback',
      icon: SvgNegativeFeedback,
      value: 0,
      valueUnit: '%',
      time: '',
      title: t('dashboard.card.negative_feedback'),
    },
    {
      id: 'userSatisfactionRate',
      icon: SvgSatisfactionRate,
      value: 0,
      time: '',
      title: t('dashboard.card.user_satisfaction_rate'),
    },
  ];

  const rangeDateText = useMemo(() => {
    const text = rangeDate.map((date) => dayjs(date).format('YYYY-MM-DD'));
    if (text[0] === text[1]) return text[0];
    return text.join(' ~ ');
  }, [rangeDate]);

  const handleCardClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDateChange = (date: DatesRangeValue | null, text: string) => {
    if (date) {
      const dateTime = date.map((item) => (item && dayjs(item).utc().format()) || '');
      setRangeDate(dateTime);
    }
    setRangeDateTimeText(text);
  };

  const getMetricsData = () => {
    setLoading(true);
    const params = {
      robot_id: botId,
      start_date: rangeDate[0]?.toString() || '',
      end_date: rangeDate[1]?.toString() || '',
      size: 1000,
    };
    getMetrics(params)
      .then((res) => {
        const { items } = res.data;

        setMetricsData(items);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const download = () => {
    const fileName = 'GBase_' + botId;
    // 创建CSV的头部行

    const header = [t('dashboard.series'), rangeDateText];

    // 将每个数据对象转换为CSV格式的一行
    const rows = CardList.map((item) => {
      if (['provideFeedback', 'negativeFeedback'].includes(item.id)) {
        return [item.title, String(item.value + '%')];
      } else {
        return [item.title, String(item.value)];
      }
    });
    const data = {
      header,
      rows,
    };

    generateCSV(fileName, data);
  };

  useEffect(() => {
    if (!rangeDate.length) return;
    getMetricsData();
  }, [rangeDate]);

  /**
   * 数据处理
   * */
  const CardList = useMemo((): OverviewCardProps[] => {
    const data: TotalData = {
      totalConversations: 0,
      totalMessages: 0,
      allUsers: 0,
      provideFeedback: 0,
      negativeFeedback: 0,
      userSatisfactionRate: 0,
    };
    if (metricsData.length) {
      let likeCount = 0;
      let dislikeCount = 0;
      let allusers: string[] = [];
      metricsData.map((item) => {
        data.totalConversations += item.created_session_count;
        data.totalMessages += item.message_count;
        allusers = allusers.concat(item.new_users || [], item.old_users || []);
        likeCount += item.like_count;
        dislikeCount += item.dislike_count;
        data.userSatisfactionRate = likeCount + dislikeCount;
      });

      data.allUsers = [...new Set(allusers)].length;
      data.provideFeedback = calculatePercentage(likeCount, data.totalMessages);
      data.negativeFeedback = calculatePercentage(dislikeCount, data.totalMessages);
    }

    const newList = CARD_LIST.concat([]);
    newList.map((item) => {
      item.value = data[item.id as TotalDataKeys];
      item.time = rangeDateTimeText;
    });
    return newList;
  }, [metricsData, rangeDateTimeText, t, rangeDate]);

  /**
   * 根据起始时间和结束时间计算出X轴上的需要日期数组值
   */
  const ChartTimes = useMemo(() => {
    const times = generateDateRange(rangeDate[0], rangeDate[1]);
    return times;
  }, [rangeDate]);

  return (
    <>
      <ContainerHeader title={t('menu.bot.dashboard')} description={t('page.dashboard_description')} />
      {!isLocalMode && <WidgetInstallTip />}
      <Stack gap={40}>
        <section>
          <Group justify="space-between">
            <Text size={'xl'} fw={400}>
              {t('dashboard.overview')}
            </Text>
            <Group>
              <Text size={'sm'} className="flex items-center gap-2 text-[#667085]">
                {t('dashboard.report_generated_on')}{' '}
                {loading ? (
                  <Loader size="sm" variant="dots" />
                ) : (
                  <Text span inherit className="text-violet-500">
                    {rangeDateText}
                  </Text>
                )}
              </Text>
              <DateSelector onDateChange={handleDateChange} />
              <Button leftSection={<SvgDownload />} onClick={download} h={'auto'} p={16} radius={12}>
                <Text size="sm">{t('dashboard.download_report')}</Text>
              </Button>
            </Group>
          </Group>
        </section>

        <section>
          <SimpleGrid spacing="xl" verticalSpacing="xl" cols={{ base: 3, sm: 1, lg: 2 }}>
            {CardList.map((item, index) => {
              return (
                <OverviewCard
                  key={index}
                  {...item}
                  onClick={() => {
                    const id = ![3, 4, 5].includes(index) ? index : 3;
                    handleCardClick(`section-${id}`);
                  }}
                />
              );
            })}
          </SimpleGrid>
        </section>

        <ChartContext.Provider
          value={{ times: ChartTimes, datas: metricsData, timesText: rangeDateTimeText, rangeDate: rangeDate }}
        >
          <Stack>
            <div id="section-0">
              <TotalConversations />
            </div>

            <div>
              <TotalConversationsHeatmap />
            </div>

            <div id="section-1">
              <TotalMessages />
            </div>
            <div id="section-3">
              <ChatSatisfaction />
            </div>
            <div id="section-2">
              <AllUsers />
            </div>
            <div id="section-5">
              <PopularityInteractions />
            </div>
          </Stack>
        </ChartContext.Provider>
      </Stack>
    </>
  );
};

export default Dashboard;
